import React, { useState } from 'react';
import { authHeaderOrEmpty } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const KOMERCES_PRIMARY = '#007BFF';
const KOMERCES_SECONDARY = '#0056B3';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const ColorField = ({ label, hint, value, fallback, onChange, testId }) => (
  <div className="w-full md:flex-1 md:min-w-[220px]">
    <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1.5">{label}</label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={HEX_RE.test(value || '') ? value : fallback}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer bg-white p-1"
        data-testid={`${testId}-picker`}
      />
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={fallback}
        maxLength={7}
        className={`w-28 px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/40 ${
          value && !HEX_RE.test(value) ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-white'
        }`}
        data-testid={`${testId}-hex`}
      />
    </div>
    <p className="text-xs text-slate-400 mt-1">{hint}</p>
  </div>
);

export const AgencyColorsCard = ({ agence, onSaved }) => {
  const bs = agence?.branding_settings || {};
  const [primary, setPrimary] = useState(bs.couleur_primaire || '');
  const [secondary, setSecondary] = useState(bs.couleur_secondaire || '');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const dirty = (primary || '') !== (bs.couleur_primaire || '') || (secondary || '') !== (bs.couleur_secondaire || '');
  const invalid = (primary && !HEX_RE.test(primary)) || (secondary && !HEX_RE.test(secondary));

  const save = async (payload) => {
    setSaving(true);
    setFeedback(null);
    try {
      const response = await fetch(`${API_URL}/api/my-agence/branding/colors`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaderOrEmpty(),
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        setFeedback({ type: 'error', text: data.detail || 'Erreur lors de la sauvegarde' });
        return;
      }
      setPrimary(data.couleur_primaire || '');
      setSecondary(data.couleur_secondaire || '');
      setFeedback({ type: 'success', text: 'Couleurs enregistrées' });
      onSaved?.(data);
    } catch {
      setFeedback({ type: 'error', text: 'Erreur de connexion' });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => save({ couleur_primaire: primary || null, couleur_secondaire: secondary || null });
  const handleReset = () => save({ couleur_primaire: null, couleur_secondaire: null });

  const effectivePrimary = HEX_RE.test(primary) ? primary : KOMERCES_PRIMARY;
  const effectiveSecondary = HEX_RE.test(secondary) ? secondary : (HEX_RE.test(primary) ? primary : KOMERCES_SECONDARY);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6" data-testid="agency-colors-card">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <i className="bi bi-palette text-slate-500"></i>
          Couleurs de l'agence
        </h2>
        {feedback && (
          <span
            className={`text-sm font-medium ${feedback.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}
            data-testid="agency-colors-feedback"
          >
            <i className={`bi ${feedback.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'} mr-1`}></i>
            {feedback.text}
          </span>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Appliquées sur vos documents (fiches PDF, emails clients) dès maintenant.
        Laissez vide pour utiliser les couleurs Komerces par défaut.
      </p>

      <div className="flex flex-wrap items-end gap-6">
        <ColorField
          label="Couleur primaire"
          hint="Titres et éléments principaux des documents"
          value={primary}
          fallback={KOMERCES_PRIMARY}
          onChange={setPrimary}
          testId="agency-color-primary"
        />
        <ColorField
          label="Couleur secondaire"
          hint="Accents et soulignés de sections"
          value={secondary}
          fallback={KOMERCES_SECONDARY}
          onChange={setSecondary}
          testId="agency-color-secondary"
        />

        {/* Aperçu */}
        <div className="w-full md:flex-1 md:min-w-[200px]">
          <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1.5">Aperçu</label>
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid="agency-colors-preview">
            <div className="text-sm font-bold mb-1" style={{ color: effectivePrimary }}>
              {agence?.nom || 'Votre agence'}
            </div>
            <div className="h-1 w-16 rounded-full mb-2" style={{ backgroundColor: effectiveSecondary }}></div>
            <div className="text-xs text-slate-500">En-tête de vos fiches PDF</div>
          </div>
        </div>

        <div className="w-full md:w-auto flex flex-col md:flex-row items-stretch md:items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving || !dirty || invalid}
            className="w-full md:w-auto px-4 h-11 md:h-auto md:py-2 bg-[#28A745] hover:bg-[#218838] text-white rounded-lg font-medium text-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="agency-colors-save-btn"
          >
            {saving ? <i className="bi bi-arrow-repeat animate-spin"></i> : <i className="bi bi-check-lg"></i>}
            Enregistrer
          </button>
          {(bs.couleur_primaire || bs.couleur_secondaire || primary || secondary) && (
            <button
              onClick={handleReset}
              disabled={saving}
              className="px-4 py-2 border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg font-medium text-sm transition disabled:opacity-50"
              title="Revenir aux couleurs Komerces par défaut"
              data-testid="agency-colors-reset-btn"
            >
              Réinitialiser
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgencyColorsCard;
